import { useState } from "react";
import type { Prospect } from "./ProspectCard";

/** Copia texto al portapapeles (con respaldo para navegadores que bloquean la API moderna). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // seguimos con el respaldo
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export type ComposerState = {
  prospect: Prospect;
  text: string;
  copied: boolean;
};

type Props = {
  state: ComposerState;
  sent: boolean;
  busy: boolean;
  onClose: () => void;
  onReopen: () => void;
  onMarkSent: () => void;
  /** Informa el resultado de volver a copiar. */
  onCopied: (ok: boolean) => void;
};

/**
 * Panel flotante del flujo "Abrir y escribir": el perfil se abre en otra pestaña, el mensaje ya está
 * copiado y aquí queda a la vista hasta que la persona lo envíe y lo marque como enviado.
 * Nada se envía desde Vívelo: el mensaje sale desde la cuenta real de quien lo pega.
 */
export default function OpenAndWritePanel({ state, sent, busy, onClose, onReopen, onMarkSent, onCopied }: Props) {
  const { prospect, text, copied } = state;
  const [again, setAgain] = useState(false);

  const copyAgain = async () => {
    const ok = await copyText(text);
    onCopied(ok);
    setAgain(ok);
    window.setTimeout(() => setAgain(false), 2000);
  };

  return (
    <div
      role="dialog"
      aria-label={`Escribir a ${prospect.name}`}
      className="fixed z-50 bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[430px] rounded-2xl bg-background-50 text-foreground-800 border border-background-300 shadow-2xl p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-heading font-bold">
            <i className={copied || again ? "ri-checkbox-circle-fill text-primary-500" : "ri-error-warning-line text-accent-600"}></i>
            {copied || again ? "Mensaje copiado" : "No pude copiar solo"}
          </div>
          <p className="text-xs text-foreground-500 mt-0.5 truncate">
            Pégalo en el chat de {prospect.platform} con {prospect.name}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="w-7 h-7 shrink-0 rounded-full inline-flex items-center justify-center text-foreground-500 hover:bg-background-200 cursor-pointer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      <textarea
        readOnly
        value={text}
        onFocus={(e) => e.currentTarget.select()}
        rows={5}
        className="mt-3 w-full resize-none rounded-xl bg-background-100 border border-background-300 p-3 text-sm text-foreground-800 outline-none"
      />

      <ol className="mt-2 text-[11px] text-foreground-500 space-y-0.5 list-decimal list-inside">
        <li>En la pestaña que se abrió, toca <b>Mensaje</b>.</li>
        <li>Pega el texto (Ctrl+V, o mantén pulsado en el celular) y envíalo.</li>
        <li>Vuelve aquí y toca <b>Marcar como enviado</b>.</li>
      </ol>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={copyAgain}
          className="py-2.5 rounded-xl border border-background-300 font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer hover:border-primary-400 transition-colors"
        >
          <i className={again ? "ri-check-line" : "ri-file-copy-line"}></i> {again ? "Copiado" : "Copiar de nuevo"}
        </button>
        <button
          type="button"
          onClick={onReopen}
          className="py-2.5 rounded-xl border border-background-300 font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer hover:border-primary-400 transition-colors"
        >
          <i className="ri-external-link-line"></i> Abrir perfil otra vez
        </button>
        <button
          type="button"
          onClick={onMarkSent}
          disabled={sent || busy}
          className={[
            "col-span-2 py-3 rounded-xl font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer transition-colors disabled:cursor-not-allowed",
            sent ? "bg-secondary-500 text-white" : "bg-primary-500 text-background-950 hover:bg-primary-400 disabled:opacity-60",
          ].join(" ")}
        >
          <i className={busy ? "ri-loader-4-line animate-spin" : sent ? "ri-send-plane-fill" : "ri-send-plane-line"}></i>
          {sent ? "Enviado" : "Marcar como enviado"}
        </button>
      </div>
    </div>
  );
}